'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adoptionInquiryService } from '../../services/adoptionInquiryService';
import { applicationDocumentService, ApplicationDocument } from '../../services/applicationDocumentService';
import { emailNotificationService } from '../../services/emailNotificationService';
import { notificationService } from '../../services/notificationService';
import { AdoptionInquiry } from '../../types/models';
import { CheckCircleIcon, XMarkIcon, DocumentTextIcon, EnvelopeIcon, HomeIcon, BriefcaseIcon } from '@heroicons/react/24/outline';

export default function AdminApplicationReview() {
  const { user } = useAuth();
  const [applications, setApplications] = useState<AdoptionInquiry[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<AdoptionInquiry | null>(null);
  const [documents, setDocuments] = useState<ApplicationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [reviewData, setReviewData] = useState({
    status: '' as 'approved' | 'rejected' | '',
    responseMessage: '',
    rejectionReason: '',
    interviewDate: '',
    interviewNotes: '',
    interviewWhatsappLink: '',
    shelterNotes: '',
  });

  useEffect(() => {
    if (user) {
      loadApplications();
    }
  }, [user]);

  useEffect(() => {
    if (selectedApplication) {
      loadApplicationDocuments(selectedApplication.id);
    }
  }, [selectedApplication]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const data = await adoptionInquiryService.getAllApplications();
      setApplications(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadApplicationDocuments = async (applicationId: string) => {
    try {
      const { data, error } = await applicationDocumentService.getInquiryDocuments(applicationId);
      if (error) {
        setError(error);
        return;
      }
      const docs = data || [];
      setDocuments(docs);
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    }
  };

  const handleReviewSubmit = async () => {
    if (!selectedApplication || !user || !reviewData.status) {
      setError('Please select an action (Approve or Reject)');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const updates: Partial<AdoptionInquiry> = {
        status: reviewData.status,
        responseMessage: reviewData.responseMessage,
        shelterNotes: reviewData.shelterNotes,
        adminReviewedBy: user.id,
        adminReviewedAt: new Date().toISOString(),
      };

      if (reviewData.status === 'rejected' && reviewData.rejectionReason) {
        updates.rejectionReason = reviewData.rejectionReason;
      }

      if (reviewData.status === 'approved' && reviewData.interviewDate) {
        updates.interviewScheduledAt = reviewData.interviewDate;
        updates.interviewNotes = reviewData.interviewNotes;
        updates.interviewWhatsappLink = reviewData.interviewWhatsappLink;
      }

      const updatedApplication = await adoptionInquiryService.update(selectedApplication.id, updates);

      // if (reviewData.status === 'approved') {
      //   await emailNotificationService.sendApplicationApprovedEmail(
      //     updatedApplication,
      //     selectedApplication.contactEmail,
      //     selectedApplication.contactName,
      //     (selectedApplication as any).pet?.name || 'the pet'
      //   );

      //   if (reviewData.interviewDate) {
      //     await emailNotificationService.sendInterviewScheduledEmail(
      //       updatedApplication,
      //       selectedApplication.contactEmail,
      //       selectedApplication.contactName,
      //       (selectedApplication as any).pet?.name || 'the pet'
      //     );
      //   }
      // } else if (reviewData.status === 'rejected') {
      //   await emailNotificationService.sendApplicationRejectedEmail(
      //     updatedApplication,
      //     selectedApplication.contactEmail,
      //     selectedApplication.contactName,
      //     (selectedApplication as any).pet?.name || 'the pet'
      //   );
      // }

      // Construct notification message BEFORE clearing state
      const petName = (selectedApplication as any).pet?.name || 'the pet';
      let message = `Application Update for ${petName}\n\n`;

      if (reviewData.status === 'approved') {
        message += `Congratulations! Your application has been APPROVED.\n\n`;
        if (reviewData.interviewDate) {
          const dateObj = new Date(reviewData.interviewDate);
          const dateStr = dateObj.toLocaleDateString();
          const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          message += `Interview Schedule: ${dateStr} at ${timeStr}\n`;
        }
        if (reviewData.interviewNotes) {
          message += `Interview Notes: ${reviewData.interviewNotes}\n`;
        }
        if (reviewData.interviewWhatsappLink) {
          message += `Interview Link: ${reviewData.interviewWhatsappLink}\n`;
        }
      } else {
        message += `Update on your application.\n`;
        message += `Status: REJECTED\n`;
        if (reviewData.rejectionReason) {
          message += `Reason: ${reviewData.rejectionReason}\n`;
        }
      }

      if (reviewData.responseMessage) {
        message += `\nMessage: ${reviewData.responseMessage}`;
      }

      // Add signature with specific contact details
      message += `\n\nBest Regards,\nPetHaven Admin Team\nEmail: exoticdomestic289@gmail.com\nPhone: +1 (231) 564-9442`;

      // NOTE: App notification is now sent via Database Trigger (handle_inquiry_update)
      // client-side call removed to prevent duplication and ensure security.

      // Copy to clipboard
      try {
        await navigator.clipboard.writeText(message);
      } catch (err) {
        // Silent failure for clipboard
      }

      // Open preferred contact method
      const contactMethod = selectedApplication.preferredContact;

      if (contactMethod === 'phone' && selectedApplication.whatsappNumber) {
        const cleanNumber = selectedApplication.whatsappNumber.replace(/[^\d+]/g, '');
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${cleanNumber}?text=${encodedMessage}`, '_blank');
      } else {
        // Default to email
        const subject = encodeURIComponent(`Application Update: ${petName}`);
        const body = encodeURIComponent(message);
        // Attempt to pre-fill 'from' - support varies by email client
        window.open(`mailto:${selectedApplication.contactEmail}?from=exoticdomestic289@gmail.com&subject=${subject}&body=${body}`, '_blank');
      }

      setSuccess(`Application ${reviewData.status} successfully. Notification copied to clipboard and ${contactMethod === 'phone' ? 'WhatsApp' : 'Email'} opened.`);
      setSelectedApplication(null);
      setReviewData({
        status: '',
        responseMessage: '',
        rejectionReason: '',
        interviewDate: '',
        interviewNotes: '',
        interviewWhatsappLink: '',
        shelterNotes: '',
      });

      await loadApplications();
    } catch (err: any) {
      setError(err.message || 'Failed to process application');
    } finally {
      setProcessing(false);
    }
  };

  const openWhatsApp = (phoneNumber: string) => {
    const cleanNumber = phoneNumber?.replace(/[^\d+]/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  const filteredApplications = filterStatus === 'all'
    ? applications
    : applications.filter(app => app.status === filterStatus);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Access Required</h2>
          <p className="text-gray-600">Please log in as an administrator to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Adoption Application Review</h1>
          <p className="text-gray-600 mt-2">Review and process adoption applications</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <XMarkIcon className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 text-sm">{error}</p>
              <button
                onClick={() => setError('')}
                className="text-red-600 text-xs underline mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-800 text-sm">{success}</p>
              <button
                onClick={() => setSuccess('')}
                className="text-green-600 text-xs underline mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Applications</h2>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-3 py-1"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <p className="text-center text-gray-500 py-8">Loading applications...</p>
                ) : filteredApplications.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No applications found</p>
                ) : (
                  filteredApplications.map(app => (
                    <button
                      key={app.id}
                      onClick={() => setSelectedApplication(app)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition ${selectedApplication?.id === app.id
                        ? 'border-secondary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{app.contactName}</p>
                          <p className="text-sm text-gray-600">
                            {(app as any).pet?.name || 'Pet Not Found'}
                          </p>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${app.status === 'pending' ? 'bg-yellow-100 text-yellow-800'
                            : app.status === 'reviewing' ? 'bg-blue-100 text-blue-800'
                              : app.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {app.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(app.createdAt || '').toLocaleDateString()}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {selectedApplication ? (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedApplication.contactName}
                      </h2>
                      <p className="text-gray-600">
                        Applying for: <strong>{(selectedApplication as any).pet?.name}</strong>
                      </p>
                    </div>
                    <span
                      className={`px-4 py-2 rounded-full text-sm font-medium ${selectedApplication.status === 'pending' ? 'bg-yellow-100 text-yellow-800'
                        : selectedApplication.status === 'reviewing' ? 'bg-blue-100 text-blue-800'
                          : selectedApplication.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                    >
                      {selectedApplication.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-2">
                          <EnvelopeIcon className="w-4 h-4 mr-2" />
                          Contact Information
                        </h3>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-600">Email: {selectedApplication.contactEmail}</p>
                          <p className="text-gray-600">Phone: {selectedApplication.contactPhone}</p>
                          <div className="flex items-center space-x-2">
                            <p className="text-gray-600">WhatsApp: {selectedApplication.whatsappNumber}</p>
                            {selectedApplication.whatsappNumber && (
                              <button
                                onClick={() => openWhatsApp(selectedApplication.whatsappNumber!)}
                                className="text-green-600 hover:text-green-700 text-xs underline"
                              >
                                Open Chat
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-2">
                          <HomeIcon className="w-4 h-4 mr-2" />
                          Housing Information
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>Type: {selectedApplication.housingType}</p>
                          <p>Has Yard: {selectedApplication.hasYard ? 'Yes' : 'No'}</p>
                          {selectedApplication.landlordName && (
                            <>
                              <p>Landlord: {selectedApplication.landlordName}</p>
                              <p>Landlord Phone: {selectedApplication.landlordPhone}</p>
                              <p>Pet Policy: {selectedApplication.petPolicyApproved ? 'Approved' : 'Not Approved'}</p>
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-2">
                          <BriefcaseIcon className="w-4 h-4 mr-2" />
                          Employment
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>Status: {selectedApplication.employmentStatus}</p>
                          {selectedApplication.employerName && (
                            <>
                              <p>Employer: {selectedApplication.employerName}</p>
                              <p>Occupation: {selectedApplication.occupation}</p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Pet Experience</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>Experience Level: {selectedApplication.experienceLevel}</p>
                          <p>Has Other Pets: {selectedApplication.hasOtherPets ? 'Yes' : 'No'}</p>
                          {selectedApplication.otherPetsDetails && (
                            <p>Details: {selectedApplication.otherPetsDetails}</p>
                          )}
                        </div>
                      </div>

                      {selectedApplication.vetName && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Veterinarian</h3>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>Name: {selectedApplication.vetName}</p>
                            <p>Phone: {selectedApplication.vetPhone}</p>
                            <p>Address: {selectedApplication.vetAddress}</p>
                          </div>
                        </div>
                      )}

                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Timeline</h3>
                        <p className="text-sm text-gray-600">{selectedApplication.adoptionTimeline}</p>
                      </div>
                    </div>
                  </div>

                  {selectedApplication.petQuestions && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Questions About Pet</h3>
                      <p className="text-sm text-gray-600">{selectedApplication.petQuestions}</p>
                    </div>
                  )}

                  {selectedApplication.concerns && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Care Plan</h3>
                      <p className="text-sm text-gray-600">{selectedApplication.concerns}</p>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="w-5 h-5 mr-2" />
                    Uploaded Documents
                  </h3>
                  {documents.length === 0 ? (
                    <p className="text-gray-500 text-sm">No documents uploaded</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {documents.map(doc => (
                        <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                          <p className="font-medium text-gray-900 text-sm mb-1">
                            {doc.documentType.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-gray-500 mb-2">{doc.fileName}</p>
                          <button
                            onClick={async () => {
                              const { url, error } = await applicationDocumentService.getSignedUrl(doc.fileUrl, 600);
                              if (!error && url) {
                                window.open(url, '_blank');
                              } else {
                                setError(error || 'Failed to open document');
                              }
                            }}
                            className="text-primary hover:text-secondary text-sm underline"
                          >
                            View Document
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedApplication.status === 'pending' || selectedApplication.status === 'reviewing' ? (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Application</h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Decision <span className="text-red-500">*</span>
                        </label>
                        <div className="flex space-x-4">
                          <button
                            onClick={() => setReviewData(prev => ({ ...prev, status: 'approved' }))}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition ${reviewData.status === 'approved' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-700 hover:border-green-500'
                              }`}
                          >
                            <CheckCircleIcon className="w-5 h-5 inline mr-2" />
                            Approve
                          </button>
                          <button
                            onClick={() => setReviewData(prev => ({ ...prev, status: 'rejected' }))}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition ${reviewData.status === 'rejected' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-300 text-gray-700 hover:border-red-500'
                              }`}
                          >
                            <XMarkIcon className="w-5 h-5 inline mr-2" />
                            Reject
                          </button>
                        </div>
                      </div>

                      {reviewData.status === 'approved' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Interview Date & Time
                            </label>
                            <input
                              type="datetime-local"
                              value={reviewData.interviewDate}
                              onChange={(e) => setReviewData(prev => ({ ...prev, interviewDate: e.target.value }))}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Interview Notes
                            </label>
                            <textarea
                              value={reviewData.interviewNotes}
                              onChange={(e) => setReviewData(prev => ({ ...prev, interviewNotes: e.target.value }))}
                              rows={3}
                              placeholder="Special instructions for the interview..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Interview WhatsApp Link
                            </label>
                            <input
                              type="url"
                              value={reviewData.interviewWhatsappLink}
                              onChange={(e) => setReviewData(prev => ({ ...prev, interviewWhatsappLink: e.target.value }))}
                              placeholder="https://chat.whatsapp.com/..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                            />
                          </div>
                        </>
                      )}

                      {reviewData.status === 'rejected' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rejection Reason
                          </label>
                          <textarea
                            value={reviewData.rejectionReason}
                            onChange={(e) => setReviewData(prev => ({ ...prev, rejectionReason: e.target.value }))}
                            rows={3}
                            placeholder="Brief explanation of why the application was rejected..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Message to Applicant
                        </label>
                        <textarea
                          value={reviewData.responseMessage}
                          onChange={(e) => setReviewData(prev => ({ ...prev, responseMessage: e.target.value }))}
                          rows={4}
                          placeholder="Additional message that will be included in the notification email..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Internal Notes (Not visible to applicant)
                        </label>
                        <textarea
                          value={reviewData.shelterNotes}
                          onChange={(e) => setReviewData(prev => ({ ...prev, shelterNotes: e.target.value }))}
                          rows={3}
                          placeholder="Internal notes for shelter staff..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                        />
                      </div>

                      <button
                        onClick={handleReviewSubmit}
                        disabled={!reviewData.status || processing}
                        className="w-full py-3 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
                      >
                        {processing ? 'Processing...' : 'Submit Review & Notify Applicant'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Information</h3>
                    <div className="space-y-3 text-sm">
                      <p><strong>Status:</strong> {selectedApplication.status}</p>
                      {selectedApplication.adminReviewedBy && (
                        <p><strong>Reviewed By:</strong> {(selectedApplication as any).reviewer?.full_name || 'Unknown'}</p>
                      )}
                      {selectedApplication.adminReviewedAt && (
                        <p><strong>Reviewed At:</strong> {new Date(selectedApplication.adminReviewedAt).toLocaleString()}</p>
                      )}
                      {selectedApplication.responseMessage && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <p className="font-medium mb-1">Message to Applicant:</p>
                          <p className="text-gray-600">{selectedApplication.responseMessage}</p>
                        </div>
                      )}
                      {selectedApplication.rejectionReason && (
                        <div className="mt-4 p-4 bg-red-50 rounded-lg">
                          <p className="font-medium mb-1 text-red-800">Rejection Reason:</p>
                          <p className="text-red-600">{selectedApplication.rejectionReason}</p>
                        </div>
                      )}
                      {selectedApplication.shelterNotes && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <p className="font-medium mb-1 text-blue-800">Internal Notes:</p>
                          <p className="text-primary">{selectedApplication.shelterNotes}</p>
                        </div>
                      )}
                      {selectedApplication.interviewScheduledAt && (
                        <div className="mt-4 p-4 bg-green-50 rounded-lg">
                          <p className="font-medium mb-1 text-green-800">Interview Scheduled:</p>
                          <p className="text-green-600">{new Date(selectedApplication.interviewScheduledAt).toLocaleString()}</p>
                          {selectedApplication.interviewWhatsappLink && (
                            <p className="text-green-600 mt-1">
                              Link: <a href={selectedApplication.interviewWhatsappLink} target="_blank" rel="noopener noreferrer" className="underline">{selectedApplication.interviewWhatsappLink}</a>
                            </p>
                          )}
                          {selectedApplication.interviewNotes && (
                            <p className="text-green-600 mt-2">Notes: {selectedApplication.interviewNotes}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Select an application to review</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
